import { useEffect, useMemo, useRef } from "react";
import { marked } from "marked";
import DOMPurify from "dompurify";
import "./_Styles/Manuals.css";
import {
    FaBold, FaItalic, FaCode, FaLink, FaHeading, FaListUl, FaListOl,
    FaQuoteRight, FaCheckSquare, FaTerminal, FaTable, FaInfoCircle,
    FaPen, FaColumns, FaEye, FaSave, FaImage, FaStrikethrough,
    FaMinus, FaIndent, FaOutdent, FaChevronDown,
} from "react-icons/fa";

const MODES = [
    { id: "write", label: "Escribir", icon: <FaPen /> },
    { id: "split", label: "Dividido", icon: <FaColumns /> },
    { id: "preview", label: "Vista", icon: <FaEye /> },
];

const onSlug = (value = "") => value
    .toLowerCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "") || "seccion";

export const renderMarkdownHtml = (value = "", empty = "") => {
    const source = String(value || empty || "");
    const raw = marked.parse(source, {
        gfm: true,
        breaks: true,
    });

    return DOMPurify.sanitize(raw, {
        ADD_ATTR: ["target", "rel", "checked", "disabled"],
    });
};

export const getMarkdownOutline = (value = "") => {
    const lines = String(value || "").split("\n");
    const outline = [];
    let inFence = false;

    lines.forEach((line) => {
        if (/^\s*```/.test(line)) {
            inFence = !inFence;
            return;
        }
        if (inFence) return;

        const match = line.match(/^(#{1,6})\s+(.+?)\s*#*$/);
        if (!match) return;
        outline.push({
            level: match[1].length,
            title: match[2].replace(/[*_`~]/g, "").trim(),
            index: outline.length,
        });
    });

    return outline;
};

const getLineBounds = (value, start, end) => {
    const lineStart = value.lastIndexOf("\n", Math.max(0, start - 1)) + 1;
    const nextBreak = value.indexOf("\n", end);
    const lineEnd = nextBreak === -1 ? value.length : nextBreak;
    return { lineStart, lineEnd };
};

export const ManualMarkdownEditor = ({
    value = "",
    onChange,
    mode = "split",
    onModeChange,
    onSave,
    scrollHeadingIndex = null,
    placeholder = "Escribi tus notas, comandos, ideas y ejemplos...",
    readOnly = false,
    minHeight = 420,
    ariaLabel = "Editor Markdown",
}) => {
    const textareaRef = useRef(null);
    const previewRef = useRef(null);
    const effectiveMode = readOnly ? "preview" : mode;

    const html = useMemo(() => renderMarkdownHtml(value), [value]);

    const emit = (next) => onChange?.(next);

    const restoreSelection = (start, end = start) => requestAnimationFrame(() => {
        const area = textareaRef.current;
        if (!area) return;
        area.focus();
        area.selectionStart = start;
        area.selectionEnd = end;
    });

    const replaceSelection = (before, after = before, placeholderText = "texto") => {
        const area = textareaRef.current;
        if (!area) return;
        const start = area.selectionStart;
        const end = area.selectionEnd;
        const selected = value.slice(start, end) || placeholderText;
        const next = `${value.slice(0, start)}${before}${selected}${after}${value.slice(end)}`;
        emit(next);
        restoreSelection(start + before.length, start + before.length + selected.length);
    };

    const insertLink = () => {
        const area = textareaRef.current;
        if (!area) return;
        const start = area.selectionStart;
        const end = area.selectionEnd;
        const label = value.slice(start, end) || "texto del enlace";
        const url = window.prompt("URL del enlace", "https://");
        if (url === null) return;
        const href = String(url || "").trim() || "https://";
        const snippet = `[${label}](${href})`;
        emit(`${value.slice(0, start)}${snippet}${value.slice(end)}`);
        restoreSelection(start + 1, start + 1 + label.length);
    };

    const insertImage = () => {
        const area = textareaRef.current;
        if (!area) return;
        const start = area.selectionStart;
        const end = area.selectionEnd;
        const alt = value.slice(start, end) || "descripcion de imagen";
        const url = window.prompt("URL de la imagen", "https://");
        if (url === null) return;
        const src = String(url || "").trim() || "https://";
        const snippet = `![${alt}](${src})`;
        emit(`${value.slice(0, start)}${snippet}${value.slice(end)}`);
        restoreSelection(start + 2, start + 2 + alt.length);
    };

    const replaceLines = (transform) => {
        const area = textareaRef.current;
        if (!area) return;
        const start = area.selectionStart;
        const end = area.selectionEnd;
        const { lineStart, lineEnd } = getLineBounds(value, start, end);
        const block = value.slice(lineStart, lineEnd);
        const nextBlock = transform(block.split("\n")).join("\n");
        emit(`${value.slice(0, lineStart)}${nextBlock}${value.slice(lineEnd)}`);
        restoreSelection(lineStart, lineStart + nextBlock.length);
    };

    const cleanListPrefix = (line = "") => {
        const match = String(line).match(/^(\s*)(?:(?:[-+*]|\d+\.)\s+)(?:\[[ xX]\]\s+)?(.*)$/);
        return match ? { indent: match[1], content: match[2] } : { indent: String(line).match(/^\s*/)?.[0] || "", content: String(line).trimStart() };
    };

    const bulletLines = () => replaceLines((lines) => lines.map((line) => {
        const { indent, content } = cleanListPrefix(line);
        return `${indent}- ${content || "elemento"}`;
    }));

    const numberLines = () => replaceLines((lines) => lines.map((line, index) => {
        const { indent, content } = cleanListPrefix(line);
        return `${indent}${index + 1}. ${content || "elemento"}`;
    }));

    const checklistLines = () => replaceLines((lines) => lines.map((line) => {
        const { indent, content } = cleanListPrefix(line);
        return `${indent}- [ ] ${content || "tarea"}`;
    }));

    const quoteLines = () => replaceLines((lines) => lines.map((line) => {
        const clean = String(line).replace(/^(\s*)>\s?/, "$1");
        return `> ${clean.trimStart() || "cita"}`;
    }));

    const applyHeading = (level) => replaceLines((lines) => lines.map((line) => {
        const clean = line.replace(/^\s*#{1,6}\s+/, "");
        return `${"#".repeat(level)} ${clean || "Titulo"}`;
    }));

    const indentLines = (delta = 1) => replaceLines((lines) => lines.map((line) => (
        delta > 0 ? `  ${line}` : line.replace(/^ {1,2}/, "")
    )));

    const insertBlock = (snippet, { select = null } = {}) => {
        const area = textareaRef.current;
        const start = area?.selectionStart ?? value.length;
        const end = area?.selectionEnd ?? value.length;
        const before = value.slice(0, start);
        const after = value.slice(end);
        const leftSep = before && !before.endsWith("\n") ? "\n\n" : (before.endsWith("\n") && !before.endsWith("\n\n") ? "\n" : "");
        const rightSep = after && !after.startsWith("\n") ? "\n\n" : "";
        const next = `${before}${leftSep}${snippet}${rightSep}${after}`;
        emit(next);
        const base = before.length + leftSep.length;
        if (select) restoreSelection(base + select[0], base + select[1]);
        else restoreSelection(base + snippet.length);
    };

    const insertCodeBlock = () => {
        const area = textareaRef.current;
        const selected = area ? value.slice(area.selectionStart, area.selectionEnd) : "";
        const body = selected || "# tu comando, ejemplo o codigo";
        insertBlock(`\`\`\`bash\n${body}\n\`\`\``);
    };

    const insertTable = () => insertBlock(
        "| Columna | Valor | Notas |\n| --- | --- | --- |\n| Ejemplo | Dato | Descripcion |\n| Otro | Valor | Detalle |",
    );

    const insertDetails = () => insertBlock(
        "<details>\n<summary>Ver detalle</summary>\n\nContenido ampliado.\n\n</details>",
    );

    const continueListOnEnter = (event) => {
        const area = textareaRef.current;
        if (!area || area.selectionStart !== area.selectionEnd) return false;
        const cursor = area.selectionStart;
        const lineStart = value.lastIndexOf("\n", Math.max(0, cursor - 1)) + 1;
        const line = value.slice(lineStart, cursor);
        const match = line.match(/^(\s*)([-+*]|\d+\.)\s+(\[[ xX]\]\s+)?(.*)$/);
        if (!match) return false;

        event.preventDefault();
        const [, indent, marker, task, content] = match;
        if (!content.trim()) {
            const next = `${value.slice(0, lineStart)}${indent}${value.slice(cursor)}`;
            emit(next);
            restoreSelection(lineStart + indent.length);
            return true;
        }

        const nextMarker = /^\d+\.$/.test(marker) ? `${Number(marker.slice(0, -1)) + 1}.` : marker;
        const continuation = `\n${indent}${nextMarker} ${task ? "[ ] " : ""}`;
        emit(`${value.slice(0, cursor)}${continuation}${value.slice(cursor)}`);
        restoreSelection(cursor + continuation.length);
        return true;
    };

    const onKeyDown = (event) => {
        const ctrl = event.ctrlKey || event.metaKey;

        if (ctrl && event.key.toLowerCase() === "s") {
            event.preventDefault();
            onSave?.();
            return;
        }
        if (ctrl && event.key.toLowerCase() === "b") {
            event.preventDefault();
            replaceSelection("**");
            return;
        }
        if (ctrl && event.key.toLowerCase() === "i") {
            event.preventDefault();
            replaceSelection("_");
            return;
        }
        if (ctrl && event.key.toLowerCase() === "k") {
            event.preventDefault();
            insertLink();
            return;
        }
        if (event.key === "Enter" && continueListOnEnter(event)) return;
        if (event.key === "Tab") {
            event.preventDefault();
            const area = textareaRef.current;
            if (!area) return;
            const hasMultiLineSelection = value.slice(area.selectionStart, area.selectionEnd).includes("\n");
            if (hasMultiLineSelection || event.shiftKey) {
                indentLines(event.shiftKey ? -1 : 1);
                return;
            }
            const start = area.selectionStart;
            const end = area.selectionEnd;
            emit(`${value.slice(0, start)}  ${value.slice(end)}`);
            restoreSelection(start + 2);
        }
    };

    useEffect(() => {
        const root = previewRef.current;
        if (!root) return;

        root.querySelectorAll("a").forEach((anchor) => {
            anchor.setAttribute("target", "_blank");
            anchor.setAttribute("rel", "noreferrer noopener");
        });

        root.querySelectorAll("img").forEach((image) => {
            image.setAttribute("loading", "lazy");
            image.setAttribute("decoding", "async");
            image.setAttribute("title", image.getAttribute("alt") || "Abrir imagen");
            image.onclick = () => {
                if (image.src) window.open(image.src, "_blank", "noopener,noreferrer");
            };
        });

        root.querySelectorAll("pre").forEach((pre) => {
            if (pre.querySelector(":scope > .LStudio__manual_code_copy")) return;
            const code = pre.querySelector("code");
            const languageClass = Array.from(code?.classList || []).find((item) => item.startsWith("language-"));
            const language = languageClass?.replace("language-", "") || "code";

            const label = document.createElement("span");
            label.className = "LStudio__manual_code_lang";
            label.textContent = language;
            pre.appendChild(label);

            const button = document.createElement("button");
            button.type = "button";
            button.className = "LStudio__manual_code_copy";
            button.textContent = "Copiar";
            button.onclick = async () => {
                try {
                    await navigator.clipboard.writeText(code?.textContent || "");
                    button.textContent = "Copiado";
                    window.setTimeout(() => { button.textContent = "Copiar"; }, 1200);
                } catch {
                    button.textContent = "Error";
                }
            };
            pre.appendChild(button);
        });

        root.querySelectorAll("blockquote").forEach((quote) => {
            const first = quote.querySelector("p");
            const text = first?.textContent?.trim() || "";
            const match = text.match(/^\[!(NOTE|TIP|WARNING|DANGER|INFO|IMPORTANT)\]\s*/i);
            if (!match || !first) return;
            quote.dataset.callout = match[1].toLowerCase();
            first.innerHTML = first.innerHTML.replace(/^\[!(NOTE|TIP|WARNING|DANGER|INFO|IMPORTANT)\]\s*/i, "");
        });

        const used = new Map();
        root.querySelectorAll("h1, h2, h3, h4, h5, h6").forEach((heading, index) => {
            const base = onSlug(heading.textContent || "seccion");
            const count = used.get(base) || 0;
            used.set(base, count + 1);
            heading.id = count ? `${base}-${count + 1}` : base;
            heading.dataset.manualHeadingIndex = String(index);
        });

        if (scrollHeadingIndex !== null && scrollHeadingIndex !== undefined) {
            const target = root.querySelector(`[data-manual-heading-index="${scrollHeadingIndex}"]`);
            target?.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    }, [html, effectiveMode, scrollHeadingIndex]);

    const canWrite = !readOnly && effectiveMode !== "preview";
    const ToolButton = ({ title, icon, label = null, onClick, disabled = !canWrite, className = "" }) => (
        <button type="button" className={className} title={title} aria-label={title} disabled={disabled} onClick={onClick}>
            {icon}{label && <span>{label}</span>}
        </button>
    );

    return (
        <div className="LStudio__manual_editor" data-mode={effectiveMode} data-readonly={readOnly ? "true" : "false"}
            style={{ "--manual-editor-min-height": `${Number(minHeight) || 420}px` }}>
            {!readOnly && <div className="LStudio__manual_editor_toolbar">
                <div className="LStudio__manual_editor_tools" aria-label="Formato Markdown">
                    <div className="LStudio__manual_editor_group">
                        <ToolButton title="Titulo H1" icon={<FaHeading />} label="H1" onClick={() => applyHeading(1)} />
                        <ToolButton title="Titulo H2" icon={<FaHeading />} label="H2" onClick={() => applyHeading(2)} />
                        <ToolButton title="Titulo H3" icon={<FaHeading />} label="H3" onClick={() => applyHeading(3)} />
                        <ToolButton title="Titulo H4" icon={<FaHeading />} label="H4" onClick={() => applyHeading(4)} />
                        <ToolButton title="Titulo H5" icon={<FaHeading />} label="H5" onClick={() => applyHeading(5)} />
                        <ToolButton title="Titulo H6" icon={<FaHeading />} label="H6" onClick={() => applyHeading(6)} />
                    </div>
                    <span className="LStudio__manual_editor_divider" />
                    <div className="LStudio__manual_editor_group">
                        <ToolButton title="Negrita (Ctrl+B)" icon={<FaBold />} onClick={() => replaceSelection("**")} />
                        <ToolButton title="Cursiva (Ctrl+I)" icon={<FaItalic />} onClick={() => replaceSelection("_")} />
                        <ToolButton title="Tachado" icon={<FaStrikethrough />} onClick={() => replaceSelection("~~")} />
                        <ToolButton title="Codigo inline" icon={<FaCode />} onClick={() => replaceSelection("`")} />
                        <ToolButton title="Enlace (Ctrl+K)" icon={<FaLink />} onClick={insertLink} />
                        <ToolButton title="Imagen por URL" icon={<FaImage />} onClick={insertImage} />
                    </div>
                    <span className="LStudio__manual_editor_divider" />
                    <div className="LStudio__manual_editor_group">
                        <ToolButton title="Lista con viñetas" icon={<FaListUl />} onClick={bulletLines} />
                        <ToolButton title="Lista numerada" icon={<FaListOl />} onClick={numberLines} />
                        <ToolButton title="Checklist" icon={<FaCheckSquare />} onClick={checklistLines} />
                        <ToolButton title="Cita" icon={<FaQuoteRight />} onClick={quoteLines} />
                        <ToolButton title="Indentar" icon={<FaIndent />} onClick={() => indentLines(1)} />
                        <ToolButton title="Quitar indentacion" icon={<FaOutdent />} onClick={() => indentLines(-1)} />
                    </div>
                    <span className="LStudio__manual_editor_divider" />
                    <div className="LStudio__manual_editor_group LStudio__manual_editor_group--blocks">
                        <ToolButton title="Bloque de codigo" icon={<FaTerminal />} label="Codigo" onClick={insertCodeBlock} className="LStudio__manual_editor_block" />
                        <ToolButton title="Callout" icon={<FaInfoCircle />} label="Callout" onClick={() => insertBlock("> [!NOTE]\n> Nota importante\n> Podes escribir varias lineas.")} className="LStudio__manual_editor_block" />
                        <ToolButton title="Tabla" icon={<FaTable />} label="Tabla" onClick={insertTable} className="LStudio__manual_editor_block" />
                        <ToolButton title="Separador" icon={<FaMinus />} label="Linea" onClick={() => insertBlock("---")} className="LStudio__manual_editor_block" />
                        <ToolButton title="Detalle desplegable" icon={<FaChevronDown />} label="Detalle" onClick={insertDetails} className="LStudio__manual_editor_block" />
                    </div>
                </div>

                <div className="LStudio__manual_editor_modes">
                    {onSave && <button type="button" title="Guardar ahora (Ctrl+S)" onClick={onSave}><FaSave /><span>Guardar</span></button>}
                    {MODES.map((item) => (
                        <button key={item.id} type="button" data-active={effectiveMode === item.id}
                            title={item.label} onClick={() => onModeChange?.(item.id)}>
                            {item.icon}<span>{item.label}</span>
                        </button>
                    ))}
                </div>
            </div>}

            <div className="LStudio__manual_editor_panes">
                {effectiveMode !== "preview" && (
                    <textarea
                        ref={textareaRef}
                        className="LStudio__manual_editor_source"
                        value={value}
                        onChange={(event) => emit(event.target.value)}
                        onKeyDown={onKeyDown}
                        placeholder={placeholder}
                        spellCheck="false"
                        autoComplete="off"
                        aria-label={ariaLabel}
                    />
                )}

                {effectiveMode !== "write" && (
                    <article
                        ref={previewRef}
                        className="LStudio__manual_editor_preview"
                        dangerouslySetInnerHTML={{ __html: html }}
                    />
                )}
            </div>
        </div>
    );
};
